import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, Mail, MapPin, Check, ArrowRight, User, Calendar,
  Car, Shield, MessageSquare,
} from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { BRAND, CATEGORIES, PROVIDERS } from "@/lib/data";
import CityAutocomplete from "@/components/shared/CityAutocomplete";
import Recaptcha, { RECAPTCHA_ENABLED } from "@/components/shared/Recaptcha";
import TermsHint from "@/components/shared/TermsHint";

export default function Contact() {
  const [params] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pickup_city: params.get("pickup_city") || "",
    pickup_date: params.get("pickup_date") || today,
    dropoff_date: params.get("dropoff_date") || today,
    car_category: params.get("car_category") || "",
    lead_driver_age: 30,
    preferred_provider: "",
    message: "",
    website: "", // honeypot
  });

  const [captchaToken, setCaptchaToken] = useState(RECAPTCHA_ENABLED ? "" : "__bypass__");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      pickup_city: params.get("pickup_city") || f.pickup_city,
      pickup_date: params.get("pickup_date") || f.pickup_date,
      dropoff_date: params.get("dropoff_date") || f.dropoff_date,
      car_category: params.get("car_category") || f.car_category,
    }));
  }, [params]);

  const set = (k) => (e) => {
    const v = k === "lead_driver_age" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error("Please complete the human verification before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, captcha_token: captchaToken });
      setDone(true);
      // Google Ads conversion — fires once per successful inquiry.
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18396448628/9ogWCIOPl-4cEPSOjsRE",
          value: 1.0,
          currency: "USD",
          transaction_id: data?.id || "",
        });
      }
      toast.success("Inquiry submitted — we'll call you shortly.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-[#0B2545] text-white pt-16 md:pt-24 pb-14 md:pb-20" data-testid="contact-hero">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-6">Contact · Quote request</div>
          <h1 className="font-serif text-[9vw] md:text-[6vw] lg:text-[5vw] leading-[1.02] tracking-[-0.02em] text-white max-w-5xl">
            Tell us about your trip.
          </h1>
          <p className="mt-6 text-white/85 text-lg md:text-xl max-w-3xl leading-relaxed">
            One form. Every major provider compared. Zero booking fees. Or call us directly &mdash; we&apos;re happy to help.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#F5F7FA]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            <a href={BRAND.phoneHref} className="block group bg-white border border-line p-6 hover:border-[#0B2545] transition-colors" data-testid="contact-phone-block">
              <div className="overline mb-3">Talk to us</div>
              <div className="font-serif text-3xl md:text-4xl text-[#0B2545] flex items-center gap-3">
                <Phone className="h-5 w-5" strokeWidth={1.6} />
                {BRAND.phone}
              </div>
              <p className="mt-2 text-[#5B6472] text-sm">Mon–Sun · 7am–11pm MST</p>
            </a>

            <a href={BRAND.emailHref} className="block group bg-white border border-line p-6 hover:border-[#0B2545] transition-colors" data-testid="contact-email-block">
              <div className="overline mb-3">Email us</div>
              <div className="font-serif text-2xl md:text-3xl text-[#0B2545] flex items-center gap-3 break-all">
                <Mail className="h-5 w-5 shrink-0" strokeWidth={1.6} />
                <span>{BRAND.email}</span>
              </div>
            </a>

            <div className="bg-white border border-line p-6">
              <div className="overline mb-3">Head office</div>
              <div className="text-[#0B2545] text-base leading-relaxed flex items-start gap-3">
                <MapPin className="h-5 w-5 shrink-0 mt-1" strokeWidth={1.6} />
                <span>{BRAND.address}</span>
              </div>
            </div>

            <div className="bg-white border border-line p-6">
              <div className="overline mb-4">Follow-up process</div>
              <ol className="space-y-4">
                {[
                  "We review your inquiry within one business hour.",
                  "We compare live availability across every major provider.",
                  "We call or email a side-by-side quote — you pick the best option.",
                ].map((s, i) => (
                  <li key={i} className="flex items-baseline gap-4 text-[#5B6472]">
                    <span className="font-mono text-xs text-[#0B2545] font-semibold">{String(i + 1).padStart(2, "0")}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-[#0B2545] p-10 md:p-14"
                data-testid="inquiry-success"
              >
                <div className="inline-flex items-center justify-center h-14 w-14 bg-[#0B2545] mb-8">
                  <Check className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight mb-5">
                  Inquiry received.
                </h3>
                <p className="text-[#5B6472] max-w-lg mb-10 text-lg leading-relaxed">
                  Thanks, {form.full_name.split(" ")[0] || "friend"}. Our team is reviewing your
                  request now and will reach out via phone or email with a provider comparison
                  within the hour.
                </p>
                <a href={BRAND.phoneHref} className="btn-primary" data-testid="success-call">
                  <Phone className="h-4 w-4" strokeWidth={2} /> Call the team now
                  <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </a>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="inquiry-form" noValidate>
                {/* Card 1 — Your details */}
                <FormCard
                  icon={<User className="h-4 w-4" strokeWidth={1.8} />}
                  step="01"
                  title="Your details"
                  subtitle="So we know who to reach — and how."
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Full name" required>
                      <input required minLength={2} type="text" value={form.full_name}
                        onChange={set("full_name")} data-testid="input-full-name"
                        placeholder="Jane Doe" className="cf-input" />
                    </Field>
                    <Field label="Phone" required>
                      <input required type="tel" value={form.phone} onChange={set("phone")}
                        data-testid="input-phone" placeholder="+1 (___) ___-____"
                        className="cf-input" />
                    </Field>
                    <Field label="Email" required className="md:col-span-2">
                      <input required type="email" value={form.email} onChange={set("email")}
                        data-testid="input-email" placeholder="jane@example.com"
                        className="cf-input" />
                    </Field>
                  </div>
                </FormCard>

                {/* Card 2 — Trip details */}
                <FormCard
                  icon={<Calendar className="h-4 w-4" strokeWidth={1.8} />}
                  step="02"
                  title="Trip details"
                  subtitle="Where and when do you need the car?"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Pickup city" required className="md:col-span-2">
                      <CityAutocomplete
                        value={form.pickup_city}
                        onChange={(v) => setForm((f) => ({ ...f, pickup_city: v }))}
                        placeholder="Start typing a US city…"
                        required
                        dataTestId="input-pickup-city"
                        inputClassName="cf-input pl-9"
                        showIcon
                      />
                    </Field>
                    <Field label="Pickup date" required>
                      <input required type="date" min={today} value={form.pickup_date}
                        onChange={set("pickup_date")} data-testid="input-pickup-date"
                        className="cf-input" />
                    </Field>
                    <Field label="Drop-off date" required>
                      <input required type="date" min={form.pickup_date} value={form.dropoff_date}
                        onChange={set("dropoff_date")} data-testid="input-dropoff-date"
                        className="cf-input" />
                    </Field>
                    <Field label="Lead driver age" required>
                      <input required type="number" min={18} max={99} value={form.lead_driver_age}
                        onChange={set("lead_driver_age")} data-testid="input-age"
                        className="cf-input" />
                    </Field>
                  </div>
                </FormCard>

                {/* Card 3 — Preferences */}
                <FormCard
                  icon={<Car className="h-4 w-4" strokeWidth={1.8} />}
                  step="03"
                  title="Preferences"
                  subtitle="All optional — leave blank and we'll suggest the best fit."
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Car category (optional)">
                      <select value={form.car_category} onChange={set("car_category")}
                        data-testid="input-category" className="cf-input">
                        <option value="">No preference — you decide</option>
                        {CATEGORIES.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Preferred provider (optional)">
                      <select value={form.preferred_provider} onChange={set("preferred_provider")}
                        data-testid="input-provider" className="cf-input">
                        <option value="">No preference — compare all</option>
                        {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </Field>
                    <Field label="Notes (optional)" className="md:col-span-2">
                      <textarea rows={4} value={form.message} onChange={set("message")}
                        data-testid="input-message"
                        className="cf-input resize-none"
                        placeholder="Pickup time, add-ons, one-way rentals, additional drivers…" />
                    </Field>
                  </div>
                </FormCard>

                {/* Card 4 — Verification & submit */}
                <FormCard
                  icon={<Shield className="h-4 w-4" strokeWidth={1.8} />}
                  step="04"
                  title="Almost done"
                  subtitle="A quick verification and we'll take it from here."
                >
                  {/* Honeypot — hidden from real users, visible only to bots */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                    <label>
                      Website
                      <input type="text" tabIndex={-1} autoComplete="off"
                        value={form.website} onChange={set("website")} />
                    </label>
                  </div>

                  <div className="flex flex-col gap-6">
                    <Recaptcha onChange={(t) => setCaptchaToken(t || "")} />

                    <p className="text-xs text-[#5B6472] flex items-start gap-2">
                      <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.8} />
                      By submitting you agree to be contacted by phone or email by the Carztohire team.
                      We never share your info with third parties.
                    </p>

                    <div className="relative inline-flex flex-col group self-start w-full sm:w-auto">
                      <button
                        type="submit"
                        disabled={submitting || !captchaToken}
                        className="btn-primary w-full sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="submit-inquiry"
                      >
                        {submitting ? "Submitting…" : "Submit inquiry"}
                        <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                      </button>
                      <TermsHint dataTestId="submit-inquiry-hint" />
                    </div>
                  </div>
                </FormCard>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .cf-input {
          width: 100%;
          background: #FFFFFF;
          color: #0B2545;
          padding: 0.85rem 1rem;
          border: 1px solid #E5E7EB;
          outline: none;
          font-family: 'Inter', sans-serif;
          font-size: 0.95rem;
          transition: border-color 0.15s, box-shadow 0.15s, background-color 0.15s;
          appearance: none;
          -webkit-appearance: none;
        }
        .cf-input:hover { border-color: #C7CDD6; }
        .cf-input:focus {
          border-color: #0B2545;
          box-shadow: 0 0 0 3px rgba(11, 37, 69, 0.08);
        }
        .cf-input::placeholder { color: #94A0AE; font-weight: 400; }
        .cf-input::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.55; }
        select.cf-input {
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%230B2545' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          padding-right: 2.5rem;
        }
      `}</style>
    </PageShell>
  );
}

function FormCard({ icon, step, title, subtitle, children }) {
  return (
    <div className="bg-white border border-line" data-testid={`form-card-${step}`}>
      <div className="flex items-start gap-4 px-6 md:px-8 pt-6 md:pt-7 pb-5 border-b border-line">
        <div className="shrink-0 h-9 w-9 md:h-10 md:w-10 border border-[#0B2545] text-[#0B2545] flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5B6472]">Step {step}</span>
          </div>
          <h3 className="font-serif text-xl md:text-2xl text-[#0B2545] mt-0.5 leading-tight">{title}</h3>
          {subtitle && <p className="text-sm text-[#5B6472] mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="px-6 md:px-8 py-6 md:py-7 relative">
        {children}
      </div>
    </div>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-mono tracking-[0.16em] uppercase text-[#5B6472] mb-2">
        {label}{required && <span className="text-[#0B2545] font-semibold ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
